import { useEffect, useSyncExternalStore } from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
} from '@microsoft/signalr';

import { WS_HUB } from '@/api/Core/endpoints/WebSockets';
import { getAccessTokenForSignalR } from '@/modules/App/providers/HttpInterceptor';
import { LocalStorageKeys } from '@/utils/constants';

export enum HUB_METHODS_NAMES {
  OperatorPresenceUpdated = 'OperatorPresenceUpdated',
  CommandCentreEventCreated = 'CommandCentreEventCreated',
  OperatorMetricsUpdated = 'OperatorMetricsUpdated',
  PositionUpdated = 'PositionUpdated',
  StateChanged = 'StateChanged',
}

export type SignalRStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'failed';

const MAX_START_ATTEMPTS = 5;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function buildHubUrl() {
  const base = `${process.env.VITE_APP_BASE_API_URL?.replace('/api/', '')}${WS_HUB}`;
  const locations = localStorage.getItem(LocalStorageKeys.LocationIds) || '';
  if (!locations) return base;
  const params = new URLSearchParams({ x_app_locations: locations });
  return `${base}?${params.toString()}`;
}

export class SignalRConnection {
  private static instance: SignalRConnection;

  private connection: HubConnection | null = null;

  private startPromise: Promise<HubConnection> | null = null;

  private status: SignalRStatus = 'idle';

  private readonly statusListeners = new Set<(status: SignalRStatus) => void>();

  public static getInstance(): SignalRConnection {
    if (!SignalRConnection.instance) {
      SignalRConnection.instance = new SignalRConnection();
    }
    return SignalRConnection.instance;
  }

  public getStatus(): SignalRStatus {
    return this.status;
  }

  public subscribeStatus(
    listener: (status: SignalRStatus) => void,
  ): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private setStatus(status: SignalRStatus) {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((listener) => listener(status));
  }

  private buildConnection(): HubConnection {
    const locationHeader =
      localStorage.getItem(LocalStorageKeys.LocationIds) || '';
    const connection = new HubConnectionBuilder()
      .withUrl(buildHubUrl(), {
        // Negotiate uses Authorization via accessTokenFactory; browser WebSockets may still
        // append ?access_token= (platform logs must redact /hubs — see BE Deployment.md).
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => getAccessTokenForSignalR(),
        // JWT auth does not need cookies; false avoids CORS clash with AllowAnyOrigin.
        withCredentials: false,
        headers: {
          'x-app-locations': locationHeader,
        },
      })
      .withAutomaticReconnect()
      .build();

    connection.onreconnecting(() => this.setStatus('reconnecting'));
    connection.onreconnected(() => this.setStatus('connected'));
    connection.onclose(() => {
      if (this.status !== 'failed') this.setStatus('idle');
    });

    return connection;
  }

  public async initializeConnection() {
    if (this.connection?.state === HubConnectionState.Connected) {
      this.setStatus('connected');
      return this.connection;
    }
    if (this.startPromise) return this.startPromise;

    this.setStatus('connecting');
    this.startPromise = this.startWithRetry()
      .then(() => {
        this.setStatus('connected');
        return this.connection!;
      })
      .catch((err) => {
        this.startPromise = null;
        this.connection = null;
        this.setStatus('failed');
        console.error(
          'WebSocket connection failed!',
          { hubUrl: buildHubUrl() },
          err,
        );
        throw err;
      });

    return this.startPromise;
  }

  private async startWithRetry() {
    let lastError: unknown;
    for (let attempt = 0; attempt < MAX_START_ATTEMPTS; attempt++) {
      try {
        // HubConnection must not be restarted after a failed start — rebuild each attempt.
        if (this.connection) {
          await this.connection.stop().catch(() => undefined);
          this.connection = null;
        }
        this.connection = this.buildConnection();
        await this.connection.start();
        console.log('WebSocket connection initialized!');
        return;
      } catch (err) {
        lastError = err;
        const backoffMs = Math.min(1000 * 2 ** attempt, 15_000);
        await delay(backoffMs);
      }
    }
    throw lastError instanceof Error
      ? lastError
      : new Error('WebSocket connection failed');
  }

  public getConnection(): HubConnection | null {
    return this.connection;
  }

  public stopConnection() {
    if (this.connection) {
      void this.connection.stop();
      this.connection = null;
      this.startPromise = null;
      this.setStatus('idle');
    }
  }
}

export const useSignalRStatus = (): SignalRStatus => {
  const signalR = SignalRConnection.getInstance();
  return useSyncExternalStore(
    (onStoreChange) => signalR.subscribeStatus(onStoreChange),
    () => signalR.getStatus(),
    () => 'idle',
  );
};

export const useWebSockets = <T = unknown,>(
  event: HUB_METHODS_NAMES,
  callback: (data: T) => void,
  enabled = true,
) => {
  useEffect(() => {
    if (!enabled) return undefined;

    let mounted = true;
    const signalRInstance = SignalRConnection.getInstance();

    signalRInstance
      .initializeConnection()
      .then((connection) => {
        if (!connection || !mounted) return;
        connection.on(event, callback);
      })
      .catch(() => undefined);

    return () => {
      mounted = false;
      const connection = signalRInstance.getConnection();
      if (connection) {
        connection.off(event, callback);
      }
    };
  }, [event, callback, enabled]);
};

/**
 * Module-level reconnect handler so site switches do not stack onreconnected callbacks.
 * Assumes a single active `useHubSiteGroup` consumer (Command Centre).
 * `siteId` is cleared on unmount so reconnect cannot ghost-rejoin after leaving the page.
 */
const siteGroupState = {
  siteId: undefined as number | undefined,
  registered: false,
};

async function joinSiteGroup(conn: HubConnection, id: number) {
  if (conn.state !== HubConnectionState.Connected) return;
  await conn.invoke('JoinSiteGroup', id);
}

/** Join / leave SignalR site group for Command Centre fan-out (`site-{id}`). */
export const useHubSiteGroup = (siteId?: number) => {
  useEffect(() => {
    siteGroupState.siteId = siteId;
    return () => {
      if (siteGroupState.siteId === siteId) {
        siteGroupState.siteId = undefined;
      }
    };
  }, [siteId]);

  useEffect(() => {
    if (!siteId) return undefined;

    let mounted = true;
    const signalRInstance = SignalRConnection.getInstance();
    let connection: HubConnection | null = null;

    signalRInstance
      .initializeConnection()
      .then(async (conn) => {
        if (!conn || !mounted) return;
        connection = conn;

        if (!siteGroupState.registered) {
          siteGroupState.registered = true;
          conn.onreconnected(() => {
            const id = siteGroupState.siteId;
            if (!id) return;
            const active = signalRInstance.getConnection();
            if (!active) return;
            joinSiteGroup(active, id).catch((err) =>
              console.error('Failed to rejoin site group after reconnect', err),
            );
          });
        }

        try {
          await joinSiteGroup(conn, siteId);
          if (!mounted) {
            await conn.invoke('LeaveSiteGroup', siteId).catch(() => undefined);
          }
        } catch (err) {
          console.error('Failed to join site group', err);
        }
      })
      .catch(() => undefined);

    return () => {
      mounted = false;
      const conn = connection ?? signalRInstance.getConnection();
      if (conn) {
        conn.invoke('LeaveSiteGroup', siteId).catch(() => undefined);
      }
    };
  }, [siteId]);
};
